import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Plus, Edit2, Trash2, ArrowUp, ArrowDown, Loader2, ExternalLink,
  Store, GraduationCap, Monitor, Shield, Gift, LayoutTemplate, UserCheck, Landmark,
  CreditCard, Zap, BarChart3, Users, Megaphone, Truck, Package, Globe,
  ShoppingCart, Wallet, Heart, Tag, FileText, Headphones, Mail, Sparkles,
  TrendingUp, Lock, Smartphone, Layers, Award, MessageCircle, Clock, ArrowUpRight,
  BookOpen, Target, Boxes, Repeat, BadgePercent, Receipt, Send, Bell,
  Calendar, Video, ClipboardCheck,
} from 'lucide-react';

// Whitelist of icons admin can pick from. Names match lucide-react exports
// so the same string used in DB can resolve to the React component.
const ICON_REGISTRY = {
  Store, GraduationCap, Monitor, Shield, Gift, LayoutTemplate, UserCheck, Landmark,
  CreditCard, Zap, BarChart3, Users, Megaphone, Truck, Package, Globe,
  ShoppingCart, Wallet, Heart, Tag, FileText, Headphones, Mail, Sparkles,
  TrendingUp, Lock, Smartphone, Layers, Award, MessageCircle, Clock, ArrowUpRight,
  BookOpen, Target, Boxes, Repeat, BadgePercent, Receipt, Send, Bell,
  Calendar, Video, ClipboardCheck,
};
const ICON_OPTIONS = Object.keys(ICON_REGISTRY).sort();

const PRESET_COLORS = [
  '#2D6CDF', '#8B5CF6', '#059669', '#10B981', '#F43F5E', '#7C3AED',
  '#06B6D4', '#0284C7', '#F59E0B', '#F97316', '#EF4444', '#14B8A6',
  '#EC4899', '#6366F1', '#D946EF', '#0EA5E9',
];

const emptyForm = { title: '', description: '', icon: 'Sparkles', color: '#2D6CDF', sort_order: 0, is_active: true };

function FeatureIcon({ name, color, size = 20 }) {
  const Icon = ICON_REGISTRY[name] || Sparkles;
  return <Icon size={size} style={{ color }} />;
}

export default function LandingFeaturesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/landing-features');
      setItems(res.data.features || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat fitur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: (items[items.length - 1]?.sort_order ?? items.length - 1) + 1 });
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      title: it.title || '',
      description: it.description || '',
      icon: it.icon || 'Sparkles',
      color: it.color || '#2D6CDF',
      sort_order: it.sort_order ?? 0,
      is_active: it.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Judul dan deskripsi wajib diisi');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/landing-features/${editingId}`, form);
        toast.success('Fitur diperbarui');
      } else {
        await api.post('/admin/landing-features', form);
        toast.success('Fitur ditambahkan');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Hapus fitur "${it.title}"?`)) return;
    try {
      await api.delete(`/admin/landing-features/${it.id}`);
      toast.success('Fitur dihapus');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/landing-features/${it.id}`, {
        title: it.title, description: it.description, icon: it.icon, color: it.color,
        sort_order: it.sort_order, is_active: !it.is_active,
      });
      fetchItems();
    } catch {
      toast.error('Gagal mengubah status');
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx];
    const b = items[target];
    try {
      await Promise.all([
        api.put(`/admin/landing-features/${a.id}`, { ...a, sort_order: b.sort_order }),
        api.put(`/admin/landing-features/${b.id}`, { ...b, sort_order: a.sort_order }),
      ]);
      fetchItems();
    } catch {
      toast.error('Gagal mengubah urutan');
    }
  };

  return (
    <div data-testid="admin-landing-features">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kelola Fitur Showcase</h1>
          <p className="text-sm text-slate-500 mt-1">Kartu fitur yang tampil di section "Semua yang Anda Butuhkan" di halaman beranda Tokomoe.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href="/#features" target="_blank" rel="noopener noreferrer"><ExternalLink size={14} className="mr-1.5" />Lihat di Landing</a>
          </Button>
          <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-feature-btn">
            <Plus size={16} className="mr-1.5" /> Tambah Fitur
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center text-slate-500"><Loader2 size={20} className="animate-spin mr-2" /> Memuat...</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-16 text-center">
            <Sparkles size={32} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-500 mb-4">Belum ada fitur. Tambahkan kartu pertama Anda.</p>
            <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"><Plus size={16} className="mr-1.5" /> Tambah Fitur</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <Card key={it.id} className={`border-slate-200 ${!it.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className="flex flex-col gap-1 pt-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Naik"><ArrowUp size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Turun"><ArrowDown size={14} /></Button>
                </div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${it.color || '#2D6CDF'}15` }}
                >
                  <FeatureIcon name={it.icon} color={it.color || '#2D6CDF'} size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{it.title}</p>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{it.description}</p>
                  <p className="text-[10px] text-slate-400 font-mono mt-1">icon: {it.icon} · color: {it.color}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-2 mr-2">
                    <Switch checked={it.is_active} onCheckedChange={() => toggleActive(it)} aria-label="Aktif" />
                    <span className="text-xs text-slate-500 hidden md:inline">{it.is_active ? 'Aktif' : 'Nonaktif'}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(it)} aria-label="Edit"><Edit2 size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(it)} aria-label="Hapus" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Fitur' : 'Tambah Fitur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Preview */}
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${form.color}15` }}>
                  <FeatureIcon name={form.icon} color={form.color} size={24} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900">{form.title || 'Judul Fitur'}</p>
                  <p className="text-sm text-slate-600">{form.description || 'Deskripsi fitur akan tampil di sini'}</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 text-center">Preview tampilan di Landing</p>
            </div>

            <div>
              <Label>Judul</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Contoh: Store Builder"
                className="mt-1"
                data-testid="feature-title-input"
              />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Penjelasan singkat tentang fitur ini"
                rows={3}
                className="mt-1"
                data-testid="feature-description-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Ikon</Label>
                <select
                  value={form.icon}
                  onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm mt-1"
                  data-testid="feature-icon-select"
                >
                  {ICON_OPTIONS.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">Pilihan dari pustaka Lucide.</p>
              </div>
              <div>
                <Label>Warna</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={form.color}
                    onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                    placeholder="#2D6CDF"
                    className="font-mono text-xs"
                  />
                  <input
                    type="color"
                    value={form.color || '#2D6CDF'}
                    onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                    className="h-10 w-12 rounded border border-slate-200 cursor-pointer"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, color: c }))}
                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${form.color?.toLowerCase() === c.toLowerCase() ? 'border-slate-900' : 'border-white shadow-sm'}`}
                      style={{ background: c }}
                      aria-label={`Pilih warna ${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 pt-3">
              <div>
                <Label>Tampilkan di Landing</Label>
                <p className="text-xs text-slate-500">Nonaktifkan untuk menyembunyikan tanpa menghapus.</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="feature-save-btn">
              {saving ? <><Loader2 size={16} className="animate-spin mr-2" /> Menyimpan...</> : editingId ? 'Simpan Perubahan' : 'Tambah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
